import { api } from '@/api/client'

export type ProductUnit =
  | 'piece'
  | 'kg'
  | 'case'
  | 'box'
  | 'pack'
  | 'liter'
  | 'bottle'
  | 'sack'

/** Με τη σειρά που εμφανίζονται στις επιλογές· οι συνηθέστερες πρώτες. */
export const UNITS: ProductUnit[] = ['piece', 'kg', 'box', 'case', 'pack', 'liter', 'bottle', 'sack']
export type ConnectionStatus = 'pending_customer' | 'pending_supplier' | 'active' | 'blocked'
export type SupplierState = 'shadow' | 'claimed'

/** Πότε μπορεί να παραδώσει ο προμηθευτής και πόσος χρόνος μένει ως το cutoff. */
export interface DeliveryWindow {
  earliestDate: string
  /** 0 = Δευτέρα … 6 = Κυριακή. Κενή λίστα σημαίνει «κάθε μέρα». */
  deliveryDays: number[]
  cutoffTime: string | null
  leadTimeDays: number
  secondsToCutoff: number | null
  cutoffPassed: boolean
}

export interface DeliverySettings {
  cutoffTime?: string | null
  deliveryDays?: number[]
  leadTimeDays?: number
}

export interface Supplier {
  id: string
  email: string
  name: string
  phone: string | null
  state: SupplierState
  connectionStatus: ConnectionStatus | null
  connectionId: string | null
  itemCount: number
  createdAt: string
  delivery: DeliveryWindow | null
}

export interface BusinessItem {
  id: string
  supplierId: string
  baseProductId: string | null
  supplierProductId: string | null
  name: string
  customName: string | null
  unit: ProductUnit
  defaultQuantity: string
  isFavorite: boolean
  sortOrder: number
  categoryId: string | null
  note: string | null
  unavailableUntil: string | null
  isAvailableToday: boolean
}

export interface BaseProduct {
  id: string
  name: string
  category: string
  defaultUnit: ProductUnit
}

export interface SupplierProduct {
  id: string
  supplierId: string
  name: string
  sku: string | null
  unit: ProductUnit
  packageSize: string | null
  price: string | null
  isActive: boolean
  unavailableUntil: string | null
  isAvailableToday: boolean
}

export const suppliersApi = {
  list: (search?: string) =>
    api.get<Supplier[]>(`/suppliers${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  get: (id: string) => api.get<Supplier>(`/suppliers/${id}`),
  create: (payload: { email: string; name: string; phone?: string }) =>
    api.post<Supplier>('/suppliers', payload),
  update: (id: string, payload: { name?: string; phone?: string } & DeliverySettings) =>
    api.patch<Supplier>(`/suppliers/${id}`, payload),
  catalog: (id: string) => api.get<SupplierProduct[]>(`/suppliers/${id}/catalog`),

  items: (supplierId: string) => api.get<BusinessItem[]>(`/suppliers/${supplierId}/items`),
  createItem: (
    supplierId: string,
    payload: {
      customName?: string
      baseProductId?: string
      supplierProductId?: string
      unit: ProductUnit
      defaultQuantity?: string
      isFavorite?: boolean
    },
  ) => api.post<BusinessItem>(`/suppliers/${supplierId}/items`, payload),
  createItemsBulk: (
    supplierId: string,
    items: {
      baseProductId?: string
      customName?: string
      unit: ProductUnit
      defaultQuantity?: string
    }[],
  ) => api.post<BusinessItem[]>(`/suppliers/${supplierId}/items/bulk`, { items }),
  updateItem: (
    itemId: string,
    payload: { customName?: string; unit?: ProductUnit; defaultQuantity?: string; isFavorite?: boolean },
  ) => api.patch<BusinessItem>(`/supplier-items/${itemId}`, payload),
  deleteItem: (itemId: string) => api.delete<void>(`/supplier-items/${itemId}`),

  acceptConnection: (connectionId: string) =>
    api.post<{ id: string; status: ConnectionStatus }>(`/connections/${connectionId}/accept`),
  blockConnection: (connectionId: string) =>
    api.post<{ id: string; status: ConnectionStatus }>(`/connections/${connectionId}/block`),
}

export const catalogApi = {
  baseProducts: (search?: string) =>
    api.get<BaseProduct[]>(`/base-products${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  categories: () => api.get<string[]>('/base-products/categories'),
}
